import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, vModelText as _vModelText, withDirectives as _withDirectives, isRef as _isRef, createTextVNode as _createTextVNode, withCtx as _withCtx, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "space-y-8" }
const _hoisted_2 = { class: "page-title" }
const _hoisted_3 = {
  key: 0,
  class: "text-sm"
}
const _hoisted_4 = /*#__PURE__*/_createElementVNode("span", { class: "mr-2 text-gray-500" }, "ID", -1)
const _hoisted_5 = { class: "card-global flex divide-x" }
const _hoisted_6 = { class: "flex-1 flex-col space-y-6" }
const _hoisted_7 = { class: "text-lg font-semibold" }
const _hoisted_8 = { class: "grid gap-4 md:grid-cols-2" }
const _hoisted_9 = { class: "flex flex-col" }
const _hoisted_10 = ["onUpdate:modelValue"]
const _hoisted_11 = { class: "flex flex-col" }
const _hoisted_12 = ["onUpdate:modelValue"]
const _hoisted_13 = { class: "flex flex-col" }
const _hoisted_14 = { class: "flex space-x-12" }
const _hoisted_15 = { class: "flex flex-col" }

import BackButton from "@/components/common/BackButton.vue";
import { computed, onMounted, reactive, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import SegmentedButtons from "@/components/common/SegmentedButtons.vue";
import Button from "@/components/common/Button.vue";
import PublicId from "@/components/common/PublicId.vue";
import ProcessTemplate from "@/types/documents/process/ProcessTemplate";
import { ProcessTemplateTypesEnum } from "@/store/modules/processTemplate/state";
import useVuelidate from "@vuelidate/core";
import { maxLength, required } from "@vuelidate/validators";
import { useStore } from "vuex";
import { ProcessTemplateActions } from "@/store/modules/processTemplate/actions";
import { ProcessTemplateGetters } from "@/store/modules/processTemplate/getters";
import { t } from "@/i18n";


export default /*#__PURE__*/_defineComponent({
  setup(__props) {

const router = useRouter();
const route = useRoute();
const store = useStore();
const processTemplateTypeOptions = computed<
  Record<ProcessTemplateTypesEnum, string>
>(() => ({
  [ProcessTemplateTypesEnum.ACTIVE]: t("processTemplateList.type.active"),
  [ProcessTemplateTypesEnum.DISABLE]: t("processTemplateList.type.disabled"),
  [ProcessTemplateTypesEnum.SYSTEM]: t("processTemplateList.type.system"),
}));
const processTemplateTypes = computed(() =>
  Object.values(processTemplateTypeOptions.value)
);
const languages = [
  { code: "ru", label: t("processTemplateForm.language.ru") },
  { code: "en", label: t("processTemplateForm.language.en") },
  { code: "kz", label: t("processTemplateForm.language.kz") },
] as const;

const state = reactive<ProcessTemplate>({
  type: ProcessTemplateTypesEnum.ACTIVE,
  name: "",
  position: null,
  actionName: "",
  nameI18n: { ru: "", en: "", kz: "" },
  actionNameI18n: { ru: "", en: "", kz: "" },
});
const selectedProcessTemplateType = computed({
  get: () =>
    processTemplateTypeOptions.value[
      state.type as ProcessTemplateTypesEnum
    ] ?? state.type,
  set: (label: string) => {
    const type = Object.entries(processTemplateTypeOptions.value).find(
      ([, value]) => value === label
    )?.[0] as ProcessTemplateTypesEnum | undefined;
    state.type = type ?? (label as ProcessTemplateTypesEnum);
  },
});
const localizedFieldRules = {
  ru: { required, maxLength: maxLength(100) },
  en: { required, maxLength: maxLength(100) },
  kz: { required, maxLength: maxLength(100) },
};
const rules = {
  type: { required },
  position: { required },
  nameI18n: localizedFieldRules,
  actionNameI18n: localizedFieldRules,
};
const $externalResults = ref({});
const submitted = ref(false);
const v$ = useVuelidate(rules, state, { $externalResults });

if (route.name === "ProcessTemplateEdit") {
  onMounted(async () => {
    await store.dispatch(ProcessTemplateActions.FETCH_ONE, route.params.id);
    const processTemplate = store.getters[ProcessTemplateGetters.GET_CURRENT];
    state.id = processTemplate.id;
    state.nameI18n = hydrateTranslations(
      processTemplate.nameI18n,
      processTemplate.name
    );
    state.type = processTemplate.type;
    state.actionNameI18n = hydrateTranslations(
      processTemplate.actionNameI18n,
      processTemplate.actionName
    );
    state.position = processTemplate.position;
  });
}

async function handleSubmit(isFormValid: boolean) {
  submitted.value = true;
  if (!isFormValid) {
    return;
  }

  state.nameI18n = normalizeTranslations(state.nameI18n);
  state.actionNameI18n = normalizeTranslations(state.actionNameI18n);
  state.name = state.nameI18n.ru;
  state.actionName = state.actionNameI18n.ru;

  route.name === "ProcessTemplateEdit"
    ? await updateProcessTemplate()
    : await createProcessTemplate();
}

function hydrateTranslations(
  translations: Record<string, string> | null | undefined,
  fallback: string
) {
  return {
    ru: translations?.ru || fallback || "",
    en: translations?.en || fallback || "",
    kz: translations?.kz || fallback || "",
  };
}

function normalizeTranslations(
  translations: Record<string, string> | null | undefined
) {
  return {
    ru: translations?.ru?.trim() || "",
    en: translations?.en?.trim() || "",
    kz: translations?.kz?.trim() || "",
  };
}

const createProcessTemplate = async () => {
  await store
    .dispatch(ProcessTemplateActions.CREATE, state)
    .then(async () => {
      await router.push({ name: "ProcessTemplateList" });
    })
    .catch((error) => {
      $externalResults.value = error.data.data;
    });
};

const updateProcessTemplate = async () => {
  await store
    .dispatch(ProcessTemplateActions.UPDATE, {
      id: state.id,
      data: state,
    })
    .then(async () => {
      await router.push({ name: "ProcessTemplateList" });
    })
    .catch((error) => {
      $externalResults.value = error.data.data;
    });
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(BackButton, {
      to: { name: 'ProcessTemplateList' },
      class: "mb-4"
    }),
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("h1", _hoisted_2, _toDisplayString(_ctx.$route.name === "ProcessTemplateEdit"
          ? _unref(t)("processTemplateForm.titleEdit")
          : _unref(t)("processTemplateForm.titleCreate")), 1),
      (_unref(state).id)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _hoisted_4,
            _createVNode(PublicId, {
              value: _unref(state).id
            }, null, 8, ["value"])
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("form", {
        onSubmit: _cache[2] || (_cache[2] = _withModifiers(($event: any) => (handleSubmit(!_unref(v$).$invalid)), ["prevent"]))
      }, [
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("div", _hoisted_6, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(languages), (language) => {
              return (_openBlock(), _createElementBlock("div", {
                key: language.code,
                class: "space-y-4"
              }, [
                _createElementVNode("h2", _hoisted_7, _toDisplayString(language.label), 1),
                _createElementVNode("div", _hoisted_8, [
                  _createElementVNode("div", _hoisted_9, [
                    _createElementVNode("label", {
                      class: _normalizeClass(["input-form-label", {
                    'p-error': _unref(v$).nameI18n[language.code].$invalid && submitted.value,
                  }])
                    }, _toDisplayString(_unref(t)("processTemplateForm.processName")), 3),
                    _withDirectives(_createElementVNode("input", {
                      "onUpdate:modelValue": ($event: any) => ((_unref(v$).nameI18n[language.code].$model) = $event),
                      type: "text",
                      maxlength: "100",
                      class: _normalizeClass(["input-form", {
                    'p-invalid':
                      _unref(v$).nameI18n[language.code].$invalid && submitted.value,
                  }])
                    }, null, 10, _hoisted_10), [
                      [_vModelText, _unref(v$).nameI18n[language.code].$model]
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_11, [
                    _createElementVNode("label", {
                      class: _normalizeClass(["input-form-label", {
                    'p-error':
                      _unref(v$).actionNameI18n[language.code].$invalid && submitted.value,
                  }])
                    }, _toDisplayString(_unref(t)("processTemplateForm.actionName")), 3),
                    _withDirectives(_createElementVNode("input", {
                      "onUpdate:modelValue": ($event: any) => ((_unref(v$).actionNameI18n[language.code].$model) = $event),
                      type: "text",
                      maxlength: "100",
                      class: _normalizeClass(["input-form", {
                    'p-invalid':
                      _unref(v$).actionNameI18n[language.code].$invalid && submitted.value,
                  }])
                    }, null, 10, _hoisted_12), [
                      [_vModelText, _unref(v$).actionNameI18n[language.code].$model]
                    ])
                  ])
                ])
              ]))
            }), 128)),
            _createElementVNode("div", _hoisted_13, [
              _createElementVNode("label", {
                class: _normalizeClass(["input-form-label", { 'p-error': _unref(v$).position.$invalid && submitted.value }])
              }, _toDisplayString(_unref(t)("processTemplateForm.position")), 3),
              _withDirectives(_createElementVNode("input", {
                type: "number",
                class: _normalizeClass(["input-form", {
                'p-invalid': _unref(v$).position.$invalid && submitted.value,
              }]),
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(v$).position.$model) = $event))
              }, null, 2), [
                [_vModelText, _unref(v$).position.$model]
              ])
            ]),
            _createElementVNode("div", _hoisted_14, [
              _createElementVNode("div", _hoisted_15, [
                _createElementVNode("label", {
                  class: _normalizeClass(["input-form-label", { 'p-error': _unref(v$).type.$invalid && submitted.value }])
                }, _toDisplayString(_unref(t)("common.type")), 3),
                _createVNode(SegmentedButtons, {
                  class: "",
                  options: _unref(processTemplateTypes),
                  modelValue: _unref(selectedProcessTemplateType),
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (_isRef(selectedProcessTemplateType) ? (selectedProcessTemplateType).value = $event : null)),
                  required: ""
                }, null, 8, ["options", "modelValue"])
              ])
            ]),
            _createElementVNode("div", null, [
              _createVNode(Button, { type: "submit" }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$route.name === "ProcessTemplateEdit"
                  ? _unref(t)("docConstructorForm.update")
                  : _unref(t)("docConstructorForm.create")), 1)
                ]),
                _: 1
              })
            ])
          ])
        ])
      ], 32)
    ])
  ], 64))
}
}

})